
import { createContext, useContext, useEffect, useState } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase, setGuestSessionId as setSupabaseGuestId } from '../../lib/supabase';
import {
    defaultPermissionsForLegacyRole,
    type StaffPermission,
    type StaffRole,
} from '../../lib/access-control';

interface AuthContextType {
    user: User | null;
    session: Session | null;
    loading: boolean;
    guestSessionId: string | null;
    userRole: 'customer' | 'admin' | 'employee' | null;
    staffRole: StaffRole | null;
    permissions: StaffPermission[];
    hasPermission: (permission: StaffPermission) => boolean;
    signInWithGoogle: () => Promise<void>;
    signOut: () => Promise<void>;
    ensureGuestSession: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType>({
    user: null,
    session: null,
    loading: true,
    guestSessionId: null,
    userRole: null,
    staffRole: null,
    permissions: [],
    hasPermission: () => false,
    signInWithGoogle: async () => { },
    signOut: async () => { },
    ensureGuestSession: async () => null,
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [session, setSession] = useState<Session | null>(null);
    const [loading, setLoading] = useState(true);
    const [guestSessionId, setGuestSessionId] = useState<string | null>(() => {
        return localStorage.getItem('guest_session_id');
    });
    const [userRole, setUserRole] = useState<'customer' | 'admin' | 'employee' | null>(null);
    const [staffRole, setStaffRole] = useState<StaffRole | null>(null);
    const [permissions, setPermissions] = useState<StaffPermission[]>([]);

    useEffect(() => {
        // 1. Check active session
        supabase.auth.getSession().then(({ data: { session } }) => {
            handleAuthStateChange(session);
        });

        // 2. Listen for changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            handleAuthStateChange(session);
        });

        return () => subscription.unsubscribe();
    }, []);

    // Force sync guest session ID to module whenever it changes
    useEffect(() => {
        console.log("AuthProvider syncing guest-session-id to module:", guestSessionId);
        setSupabaseGuestId(guestSessionId);
    }, [guestSessionId]);

    const handleAuthStateChange = async (newSession: Session | null) => {
        const previousUser = user;
        setSession(newSession);
        setUser(newSession?.user ?? null);

        if (newSession?.user) {
            await fetchUserRole(newSession.user.id);
            // If we just logged in and had a guest session, claim quotes
            if (!previousUser && guestSessionId) {
                await claimGuestQuotes(newSession.user.id, guestSessionId);
            }
        } else {
            setUserRole(null);
            setStaffRole(null);
            setPermissions([]);
            // Initialize guest session if not logged in
            if (!guestSessionId) {
                await initGuestSession();
            }
        }
        setLoading(false);
    };

    const initGuestSession = async (): Promise<string | null> => {
        try {
            // 1. Try Edge Function first (handles cookies/IP hashing)
            const { data, error } = await supabase.functions.invoke('guest-session');
            if (!error && data?.guestSessionId) {
                setGuestSessionId(data.guestSessionId);
                localStorage.setItem('guest_session_id', data.guestSessionId);
                setSupabaseGuestId(data.guestSessionId);
                return data.guestSessionId;
            }

            // 2. Fallback to direct DB insert if Edge Function is unavailable/fails
            console.warn("Edge function failed, attempting direct guest session creation...");
            const { data: directData, error: directError } = await supabase
                .from('guest_sessions')
                .insert({})
                .select()
                .single();

            if (directError) throw directError;
            if (directData?.id) {
                setGuestSessionId(directData.id);
                localStorage.setItem('guest_session_id', directData.id);
                setSupabaseGuestId(directData.id);
                return directData.id;
            }
            return null;
        } catch (e) {
            console.error("Failed to init guest session", e);
            return null;
        }
    };

    const claimGuestQuotes = async (userId: string, sessionId: string) => {
        console.log(`Claiming quotes for user ${userId} from guest session ${sessionId}`);
        const { error } = await supabase
            .from('quotes')
            .update({ user_id: userId, guest_session_id: null })
            .eq('guest_session_id', sessionId);

        if (error) {
            console.error("Failed to claim guest quotes", error);
        } else {
            setGuestSessionId(null); // Clear guest session after claiming
            localStorage.removeItem('guest_session_id');
            setSupabaseGuestId(null);
        }
    };

    const fetchUserRole = async (userId: string) => {
        let { data, error } = await supabase
            .from('profiles')
            .select('role, staff_role_id')
            .eq('id', userId)
            .single();

        // Keep sign-in working during a rolling deploy before the roles migration lands.
        if (error) {
            const legacyResult = await supabase
                .from('profiles')
                .select('role')
                .eq('id', userId)
                .single();
            data = legacyResult.data
                ? { role: legacyResult.data.role, staff_role_id: null }
                : null;
            error = legacyResult.error;
        }
        const legacyRole = (data?.role || 'customer') as 'customer' | 'admin' | 'employee';
        setUserRole(legacyRole);

        if (error || !data?.staff_role_id || legacyRole === 'customer') {
            setStaffRole(null);
            setPermissions(defaultPermissionsForLegacyRole(legacyRole));
            return;
        }

        const [roleResult, permissionResult] = await Promise.all([
            supabase
                .from('staff_roles')
                .select('id, name, slug, description, is_system, is_admin')
                .eq('id', data.staff_role_id)
                .single(),
            supabase
                .from('staff_role_permissions')
                .select('permission')
                .eq('role_id', data.staff_role_id),
        ]);

        const role = roleResult.data;
        const permissionRows = permissionResult.data;

        if (!role || permissionResult.error) {
            setStaffRole(null);
            setPermissions(defaultPermissionsForLegacyRole(legacyRole));
            return;
        }

        setStaffRole(role as StaffRole);
        setPermissions(
            role.is_admin
                ? defaultPermissionsForLegacyRole('admin')
                : ((permissionRows || []).map(row => row.permission) as StaffPermission[]),
        );
    };

    const signInWithGoogle = async () => {
        const { error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
                redirectTo: window.location.origin, // Returns to localhost or prod URL
            },
        });
        if (error) console.error("Error logging in:", error.message);
    };

    const signOut = async () => {
        await supabase.auth.signOut();
        setUserRole(null);
        setStaffRole(null);
        setPermissions([]);
    };

    const ensureGuestSession = async () => {
        if (user) return null;
        if (guestSessionId) {
            setSupabaseGuestId(guestSessionId); // Force sync
            return guestSessionId;
        }
        return await initGuestSession();
    };

    return (
        <AuthContext.Provider value={{
            user,
            session,
            loading,
            guestSessionId,
            userRole,
            staffRole,
            permissions,
            hasPermission: permission => userRole === 'admin' || permissions.includes(permission),
            signInWithGoogle,
            signOut,
            ensureGuestSession,
        }}>
            {!loading && children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
